import { MessageBarType } from '@fluentui/react';
import { useToast } from '@h2oai/ui-kit';
import React from 'react';

import { useWorkspaces } from '../../pages/Orchestrator/WorkspaceProvider';
import { useUser } from '../../utils/hooks';
import { formatError } from '../../utils/utils';
import { useAuthzService } from '../authz.hooks';
import { RoleBinding } from '../gen/ai/h2o/authorization/v1/role_binding_pb';
import { Role } from '../gen/ai/h2o/authorization/v1/role_pb';
import { useUsers } from './UsersProvider';

type OrchestratorPermission =
  | 'canViewRunnables'
  | 'canEditRunnables'
  | 'canViewWorkflows'
  | 'canEditWorkflows'
  | 'canRunWorkflows'
  | 'canViewAccessControl';

type OrchestratorPermissions = {
  [key in OrchestratorPermission]: boolean;
};

export type Permissions = {
  orchestrator?: OrchestratorPermissions;
};

type RolesContextType = {
  allRoles?: Role[];
  bootstrapRoles?: Role[];
  roleBindings?: RoleBinding[];
  currentUserRoleBindings?: RoleBinding[];
  permissions: Permissions;
  loadingPermissions: boolean;
  createRoleBinding: (role: string, userId: string, workspaceName?: string) => void;
  fetchRoleBindings: (workspaceName: string) => void;
  deleteRoleBinding: (roleBinding: RoleBinding) => void;
  createRoleBindings: (roles: string[], userId: string, workspaceName?: string) => void;
  deleteRoleBindings: (roleBindings: RoleBinding[]) => void;
  addRoleBindingToUsers: (role: string, userIds: string[]) => void;
  loadingRoleBindings: boolean;
};

export const roleBindingsParam = `//workspaceserver/`;
export const roleBindingsFilterParam = `resource='${roleBindingsParam}`;

export enum OwnerRoleKey {
  PERSONAL_WORKSPACE_OWNER = 'roles/personal-workspace-owner',
  WORKSPACE_OWNER = 'roles/workspace-owner',
}

const defaultPermissions: Permissions = {
  orchestrator: {
    canViewRunnables: false,
    canEditRunnables: false,
    canViewWorkflows: false,
    canEditWorkflows: false,
    canRunWorkflows: false,
    canViewAccessControl: false,
  },
};

const fullPermissions: Permissions = {
  orchestrator: {
    canViewRunnables: true,
    canEditRunnables: true,
    canViewWorkflows: true,
    canEditWorkflows: true,
    canRunWorkflows: true,
    canViewAccessControl: true,
  },
};

const RolesContext = React.createContext<RolesContextType | undefined>(undefined);

const excludedRoleAnnotations = ['ui.cloud.h2o.ai/hide-in-workspace-management'];

const RoleProvider = ({ children }: { children: React.ReactNode }) => {
  const authzService = useAuthzService(),
    user = useUser(),
    { addToast } = useToast(),
    { ACTIVE_WORKSPACE_NAME } = useWorkspaces(),
    { CURRENT_USER_NAME } = useUsers(),
    [allRoles, setAllRoles] = React.useState<Role[] | undefined>(),
    [bootstrapRoles, setBootstrapRoles] = React.useState<Role[] | undefined>(),
    [roleBindings, setRoleBindings] = React.useState<RoleBinding[] | undefined>(),
    [currentUserRoleBindings, setCurrentUserRoleBindings] = React.useState<RoleBinding[] | undefined>(),
    [loadingPermissions, setLoadingPermissions] = React.useState(true),
    loadStateRef = React.useRef({
      fetchRoles: false,
      fetchRoleBindings: false,
      fetchRoleBindingsByUser: false,
    }),
    [permissions, setPermissions] = React.useState<Permissions>(defaultPermissions),
    [loading, setLoading] = React.useState(true),
    evaluateLoading = () => {
      if (
        !loadStateRef.current.fetchRoles &&
        !loadStateRef.current.fetchRoleBindings &&
        !loadStateRef.current.fetchRoleBindingsByUser
      ) {
        setLoading(false);
      }
    },
    fetchRoles = React.useCallback(async () => {
      loadStateRef.current.fetchRoles = true;
      setLoading(true);
      try {
        const { roles } = await authzService.getRoles({});
        if (!roles) {
          console.error('No roles found in the response.');
          setAllRoles([]);
          return;
        }

        const allowedRoles = roles.filter(
          (role) => !excludedRoleAnnotations.some((annotation) => role?.annotations?.[annotation])
        );
        setAllRoles(allowedRoles);
      } catch (err) {
        const message = `Failed to fetch roles: ${formatError(err)}`;
        console.error(message);
        addToast({
          messageBarType: MessageBarType.error,
          message,
        });
        setAllRoles(undefined);
      } finally {
        loadStateRef.current.fetchRoles = false;
        evaluateLoading();
      }
    }, [authzService, addToast]),
    fetchRoleBindings = React.useCallback(
      async (workspaceName = ACTIVE_WORKSPACE_NAME) => {
        if (!workspaceName) return;
        loadStateRef.current.fetchRoleBindings = true;
        setLoading(true);
        try {
          const data = await authzService.getRoleBindings({
            filter: `${roleBindingsFilterParam}${workspaceName}'`,
          });
          const roleBindingItems: RoleBinding[] | undefined = data?.roleBindings || [];
          if (data && !data?.roleBindings) console.error('No role bindings found in the response.');
          setRoleBindings(roleBindingItems);
        } catch (err) {
          const message = `Failed to fetch role bindings: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          setRoleBindings(undefined);
          throw err;
        } finally {
          loadStateRef.current.fetchRoleBindings = false;
          evaluateLoading();
        }
      },
      [authzService, addToast, ACTIVE_WORKSPACE_NAME]
    ),
    fetchRoleBindingsByUser = React.useCallback(
      async (userId: string) => {
        loadStateRef.current.fetchRoleBindingsByUser = true;
        setLoading(true);
        try {
          const data = await authzService.getRoleBindings({
            filter: `subject='${userId}'`,
          });
          const roleBindingItems: RoleBinding[] | undefined = data?.roleBindings;
          if (data && !data?.roleBindings) console.error(`No role bindings for ${userId} found in the response.`);
          setCurrentUserRoleBindings(roleBindingItems);
        } catch (err) {
          const message = `Failed to fetch role bindings for user ${userId}: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          setCurrentUserRoleBindings(undefined);
        } finally {
          loadStateRef.current.fetchRoleBindingsByUser = false;
          evaluateLoading();
        }
      },
      [authzService, addToast]
    ),
    createRoleBinding = React.useCallback(
      async (role: string, userId: string, workspaceName = ACTIVE_WORKSPACE_NAME) => {
        try {
          await authzService.createRoleBinding({
            roleBinding: {
              resource: `${roleBindingsParam}${workspaceName}`,
              subject: userId,
              role,
            },
          });
          addToast({
            messageBarType: MessageBarType.success,
            message: `Role binding created successfully for ${userId}`,
          });
        } catch (err: any) {
          const message = `Failed to create role binding: ${err?.message || err}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          throw err;
        } finally {
          fetchRoleBindings(workspaceName);
        }
      },
      [authzService, addToast, ACTIVE_WORKSPACE_NAME, fetchRoleBindings]
    ),
    deleteRoleBinding = React.useCallback(
      async (roleBinding: RoleBinding) => {
        try {
          await authzService.deleteRoleBinding({
            name: roleBinding.name,
          });
          addToast({
            messageBarType: MessageBarType.success,
            message: `Role binding deleted successfully for ${roleBinding.subject}`,
          });
        } catch (err: any) {
          const message = `Failed to delete role binding: ${err?.message || err}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
          throw err;
        } finally {
          fetchRoleBindings();
        }
      },
      [authzService, addToast, fetchRoleBindings]
    ),
    createRoleBindings = React.useCallback(
      async (roles: string[], userId: string, workspaceName = ACTIVE_WORKSPACE_NAME) => {
        try {
          await Promise.all(roles.map((role) => createRoleBinding(role, userId, workspaceName)));
          await fetchRoleBindings(workspaceName);
        } catch (err: any) {
          const message = `Failed to create role binding: ${err?.message || err}`;
          console.error(message);
          addToast({ messageBarType: MessageBarType.error, message });
        }
      },
      [createRoleBinding, fetchRoleBindings]
    ),
    deleteRoleBindings = React.useCallback(
      async (roleBindings: RoleBinding[]) => {
        roleBindings.forEach(async (roleBinding) => {
          await deleteRoleBinding(roleBinding);
        });
        fetchRoleBindings();
      },
      [deleteRoleBinding, fetchRoleBindings]
    ),
    addRoleBindingToUsers = React.useCallback(
      async (role: string, userIds: string[]) => {
        userIds.forEach(async (userId) => {
          await createRoleBinding(role, userId);
        });
        fetchRoleBindings();
      },
      [createRoleBinding, fetchRoleBindings]
    );

  React.useEffect(() => {
    if (ACTIVE_WORKSPACE_NAME && !user.isPublic) {
      void fetchRoles();
      void fetchRoleBindings();
    }
  }, [ACTIVE_WORKSPACE_NAME, user]);

  React.useEffect(() => {
    if (user.isPublic) return;
    if (CURRENT_USER_NAME) void fetchRoleBindingsByUser(CURRENT_USER_NAME);
    // Fetch role bindings on workspace change as well as new owner role bindings might be created for freshly created workspaces.
  }, [CURRENT_USER_NAME, ACTIVE_WORKSPACE_NAME]);

  React.useEffect(() => {
    setBootstrapRoles(allRoles?.filter((role) => role.creator === 'bootstrap'));
  }, [allRoles]);

  React.useEffect(() => {
    if (user.isAdmin || user.isSuperAdmin) {
      setPermissions(fullPermissions);
      setLoadingPermissions(false);
      return;
    }
    const currentUserRoleBindingsForActiveWorkspace = currentUserRoleBindings?.filter(
      (roleBinding) => roleBinding.resource === `//workspaceserver/${ACTIVE_WORKSPACE_NAME}`
    );
    if (currentUserRoleBindingsForActiveWorkspace && ACTIVE_WORKSPACE_NAME) {
      if (
        currentUserRoleBindingsForActiveWorkspace.some(
          (roleBinding) =>
            roleBinding.role === OwnerRoleKey.WORKSPACE_OWNER ||
            roleBinding.role === OwnerRoleKey.PERSONAL_WORKSPACE_OWNER
        )
      ) {
        setPermissions(fullPermissions);
        setLoadingPermissions(false);
        return;
      }

      const permissions: Permissions = {
        orchestrator: {
          canViewRunnables: currentUserRoleBindingsForActiveWorkspace.some(
            (roleBinding) =>
              roleBinding.role === 'roles/orchestrator-runnable-viewer' ||
              roleBinding.role === 'roles/orchestrator-runnable-editor' ||
              roleBinding.role === 'roles/orchestrator-runnable-owner' ||
              roleBinding.role === 'roles/reader'
          ),
          canEditRunnables: currentUserRoleBindingsForActiveWorkspace.some(
            (roleBinding) =>
              roleBinding.role === 'roles/orchestrator-runnable-editor' ||
              roleBinding.role === 'roles/orchestrator-runnable-owner'
          ),
          canViewWorkflows: currentUserRoleBindingsForActiveWorkspace.some(
            (roleBinding) =>
              roleBinding.role === 'roles/orchestrator-workflow-viewer' ||
              roleBinding.role === 'roles/orchestrator-workflow-editor' ||
              roleBinding.role === 'roles/orchestrator-workflow-runner' ||
              roleBinding.role === 'roles/orchestrator-workflow-owner' ||
              roleBinding.role === 'roles/reader'
          ),
          canEditWorkflows: currentUserRoleBindingsForActiveWorkspace.some(
            (roleBinding) =>
              roleBinding.role === 'roles/orchestrator-workflow-editor' ||
              roleBinding.role === 'roles/orchestrator-workflow-owner'
          ),
          canRunWorkflows: currentUserRoleBindingsForActiveWorkspace.some(
            (roleBinding) =>
              roleBinding.role === 'roles/orchestrator-workflow-runner' ||
              roleBinding.role === 'roles/orchestrator-workflow-owner'
          ),
          canViewAccessControl: false,
        },
      };
      setPermissions(permissions);
      setLoadingPermissions(false);
    } else {
      setPermissions(defaultPermissions);
    }
  }, [currentUserRoleBindings, setPermissions, ACTIVE_WORKSPACE_NAME, user.isAdmin, user.isSuperAdmin]);

  const value = {
    allRoles,
    bootstrapRoles,
    roleBindings,
    fetchRoleBindings,
    currentUserRoleBindings,
    permissions,
    loadingPermissions,
    createRoleBinding,
    deleteRoleBinding,
    createRoleBindings,
    deleteRoleBindings,
    addRoleBindingToUsers,
    loadingRoleBindings: loading,
  };

  return <RolesContext.Provider value={value}>{children}</RolesContext.Provider>;
};

const useRoles = () => {
  const context = React.useContext(RolesContext);
  if (!context) {
    throw new Error('useRoles must be used within a RoleProvider');
  }
  return context;
};

export { RoleProvider, useRoles };
